// useWizardState hook for managing wizard navigation and state

import { useState, useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { WizardState, WizardStep, StoryType } from '../types';
import { WIZARD_STEPS, visibleSteps, previousVisibleStep } from '../constants';
import { useHapticFeedback } from './useHapticFeedback';

interface UseWizardStateProps {
  initialStep?: WizardStep;
  onStepChange?: (step: WizardStep) => void;
  enableLogging?: boolean;
  steps?: WizardStep[];
}

export function useWizardState({
  initialStep = 'story-type',
  onStepChange,
  enableLogging = false, // Default to false for performance
  steps: stepsOverride
}: UseWizardStateProps = {}) {
  const router = useRouter();
  const { hapticNavigate, hapticSuccess, hapticWarning } = useHapticFeedback();
  
  // Performance: minimal logging
  const hookInstanceId = useRef(`hook_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const renderCount = useRef(0);
  renderCount.current++;
  
  const [state, setState] = useState<WizardState>({
    currentStep: initialStep,
    storyType: null,
    completedSteps: new Set(),
    isValid: false
  });
  
  // Performance: disabled debug state tracking

  // Logging utility with debug info (minimal for performance)
  const log = useCallback((action: string, data?: any) => {
    if (enableLogging && process.env.NODE_ENV === 'development') {
      console.log(`🔧 [WIZARD HOOK ${hookInstanceId.current}]`, {
        action,
        step: state.currentStep,
        timestamp: new Date().toISOString(),
        renderCount: renderCount.current,
        ...data
      });
    }
  }, [enableLogging, state.currentStep]);

  // Get the appropriate step sequence based on story type (the wizard may supply its own multi sequence)
  const getStepSequence = useCallback(() => {
    if (stepsOverride && state.storyType === 'multi') return stepsOverride;
    return state.storyType ? WIZARD_STEPS[state.storyType] : WIZARD_STEPS.single;
  }, [state.storyType, stepsOverride]);

  // Get current step index
  const getCurrentStepIndex = useCallback(() => {
    const steps = getStepSequence();
    return steps.indexOf(state.currentStep);
  }, [state.currentStep, getStepSequence]);

  // Scroll to top when step changes
  const scrollToTop = useCallback(() => {
    // This function should only run on the client where `window` is available.
    if (typeof window !== 'undefined') {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Double-check window exists inside setTimeout (SSR safety)
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'instant' });
        }
        // Also try to scroll any wizard containers
        if (typeof document !== 'undefined') {
          const wizardContainer = document.querySelector('.wizard-scroll-container');
          if (wizardContainer) {
            wizardContainer.scrollTop = 0;
          }
        }
      }, 50);
    }
  }, []);

  // Navigate to next step (minimal logging)
  const goToNextStep = useCallback(() => {
    const steps = getStepSequence();
    const currentIndex = getCurrentStepIndex();
    
    if (currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];
      
      setState(prev => ({
        ...prev,
        currentStep: nextStep,
        completedSteps: new Set([...prev.completedSteps, prev.currentStep])
      }));
      
      log('next', { from: state.currentStep, to: nextStep });
      onStepChange?.(nextStep);
      hapticNavigate(); // Add haptic feedback
      scrollToTop(); // Scroll to top on navigation
    } else {
      hapticWarning(); // Haptic feedback when trying to go past last step
    }
  }, [getStepSequence, getCurrentStepIndex, state.currentStep, log, onStepChange, hapticNavigate, hapticWarning, scrollToTop]);

  // Navigate to previous step (never into a hidden step such as 'story-type')
  const goToPreviousStep = useCallback(() => {
    const previousStep = previousVisibleStep(getStepSequence(), state.currentStep);
    if (previousStep) {
      setState(prev => ({ ...prev, currentStep: previousStep }));
      log('back', { from: state.currentStep, to: previousStep });
      onStepChange?.(previousStep);
      hapticNavigate();
      scrollToTop();
    } else {
      hapticWarning();
    }
  }, [getStepSequence, state.currentStep, log, onStepChange, hapticNavigate, hapticWarning, scrollToTop]);

  // Jump to specific step (minimal logging)
  const goToStep = useCallback((step: WizardStep) => {
    const steps = getStepSequence();
    
    if (steps.includes(step)) {
      setState(prev => ({
        ...prev,
        currentStep: step
      }));
      
      log('jump', { from: state.currentStep, to: step });
      onStepChange?.(step);
      scrollToTop(); // Scroll to top on navigation
    }
  }, [getStepSequence, state.currentStep, log, onStepChange, scrollToTop]);

  // Set story type (minimal logging)
  const setStoryType = useCallback((type: StoryType) => {
    setState(prev => ({
      ...prev,
      storyType: type
    }));
    
    log('setStoryType', { type });
  }, [log]);

  // Mark current step as completed (minimal logging)
  const markStepCompleted = useCallback((step?: WizardStep) => {
    const stepToMark = step || state.currentStep;
    
    setState(prev => ({
      ...prev,
      completedSteps: new Set([...prev.completedSteps, stepToMark])
    }));
    
    log('markCompleted', { step: stepToMark });
    hapticSuccess(); // Add success haptic when completing a step
  }, [state.currentStep, log, hapticSuccess]);

  // First step and progress count only the steps a visitor sees
  const shownSteps = visibleSteps(getStepSequence());
  const isFirstStep = shownSteps.indexOf(state.currentStep) <= 0;
  const isLastStep = getCurrentStepIndex() === getStepSequence().length - 1;
  const progress = ((Math.max(0, shownSteps.indexOf(state.currentStep)) + 1) / shownSteps.length) * 100;

  // Performance: disabled lifecycle logging and callback tracking
  
  // Touch gesture support
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && !isLastStep) {
      goToNextStep();
    }
    if (isRightSwipe && !isFirstStep) {
      goToPreviousStep();
    }
  }, [touchStart, touchEnd, isFirstStep, isLastStep, goToNextStep, goToPreviousStep]);

  return {
    // State
    currentStep: state.currentStep,
    storyType: state.storyType,
    completedSteps: state.completedSteps,
    isValid: state.isValid,
    
    // Navigation
    goToNextStep,
    goToPreviousStep,
    goToStep,
    
    // Actions
    setStoryType,
    markStepCompleted,
    
    // Status
    isFirstStep,
    isLastStep,
    progress,
    
    // Touch handlers
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd
    }
  };
}
